import React from "react";
import { gql } from "@apollo/client";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import Text from "@src/lib/ui/Text";
import { Badge } from "@src/lib/ui/Badge";
import {
  useOrgGuestFeedbackSyncsQuery,
  OrgGuestFeedbackSyncFragment,
} from "./GuestFeedbackSyncsTab.generated";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import { formatDateTime } from "@src/lib/utils/dates";

gql`
  fragment OrgGuestFeedbackSync on OrgGuestFeedbackSync {
    id
    startedAt
    completedAt
    feedbacks {
      id
    }
    orgGuestFeedbackIntegration {
      id
      googleBusinessConfig {
        id
      }
      ovationIntegrationConfig {
        id
      }
      tattleIntegrationConfig {
        id
      }
    }
  }

  query OrgGuestFeedbackSyncs {
    OrgGuestFeedbackSyncs {
      objects {
        ...OrgGuestFeedbackSync
      }
      totalCount
    }
  }
`;

const getIntegrationType = (sync: OrgGuestFeedbackSyncFragment): string => {
  const integration = sync.orgGuestFeedbackIntegration;
  if (integration.googleBusinessConfig) {
    return "Google Business";
  }
  if (integration.ovationIntegrationConfig) {
    return "Ovation";
  }
  if (integration.tattleIntegrationConfig) {
    return "Tattle";
  }
  return "Unknown";
};

const getSyncStatus = (
  sync: OrgGuestFeedbackSyncFragment,
): { label: string; variant: "success" | "secondary" | "default" } => {
  if (sync.completedAt) {
    return { label: "Completed", variant: "success" };
  }
  return { label: "In Progress", variant: "default" };
};

const GuestFeedbackSyncsTab: React.FC = () => {
  const { _toggleSidebar } = useDetailSidebarContext();
  const { data, loading } = useOrgGuestFeedbackSyncsQuery();

  const syncs = data?.OrgGuestFeedbackSyncs?.objects ?? [];

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (syncs.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>No syncs yet</EmptyStateTitle>
        <EmptyStateDescription>
          Integration syncs will appear here once data has been fetched from
          your connected integrations.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      <Text variant="fine-print-description" className="text-muted-foreground">
        {syncs.length} sync{syncs.length !== 1 ? "s" : ""}
      </Text>

      <HtmlTable className="w-full">
        <HtmlTableHeader>
          <HtmlTableRow>
            <HtmlTableHead className="w-[200px]">Sync ID</HtmlTableHead>
            <HtmlTableHead className="w-[150px]">Integration</HtmlTableHead>
            <HtmlTableHead className="w-[180px]">Started</HtmlTableHead>
            <HtmlTableHead className="w-[180px]">Completed</HtmlTableHead>
            <HtmlTableHead className="w-[100px]">Status</HtmlTableHead>
            <HtmlTableHead className="w-[100px]">Reviews</HtmlTableHead>
          </HtmlTableRow>
        </HtmlTableHeader>
        <HtmlTableBody>
          {syncs.map((sync) => {
            const status = getSyncStatus(sync);
            return (
              <HtmlTableRow
                key={sync.id}
                className="cursor-pointer"
                onClick={(e) =>
                  _toggleSidebar(
                    e,
                    sync.id,
                    DetailSidebarType.GuestFeedbackSync,
                    "guest-feedback-syncs",
                  )
                }
              >
                <HtmlTableCell>
                  <div className="flex items-center gap-2">
                    <ArrowsRefreshIcon className="text-gray-7" />
                    <Text variant="compact-title">
                      {sync.id.slice(0, 8)}...
                    </Text>
                  </div>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text variant="compact-description">
                    {getIntegrationType(sync)}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {formatDateTime(new Date(sync.startedAt))}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {sync.completedAt
                      ? formatDateTime(new Date(sync.completedAt))
                      : "--"}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {sync.feedbacks.length}
                  </Text>
                </HtmlTableCell>
              </HtmlTableRow>
            );
          })}
        </HtmlTableBody>
      </HtmlTable>
    </div>
  );
};

export default GuestFeedbackSyncsTab;
